import React, { useState, useEffect } from "react";
import { Grid, Typography, Box, Button, FormControl, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../../../store";
import { updateTestDriveBooking } from "../../../../../actions/testDriveBooking/actions";
import { DeliveryType, TestDriveBookingSteps, testDriveExperienceOptions, TestDriveExperiences, TestDriveTypes, testDriveTypesOptions } from "../../../../../reducers/testDriveBooking/types";
import { getVehicleTypeIcon, getFeatureTypeIcon, formatGraphQLErrorMessage } from "../../../../utils";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../utils";
import CheckRounded from "@material-ui/icons/CheckCircleRounded";
import { CalendarTimeSlotSelector } from "../VehicleConfirmation/SlotPicker";
import { useLazyQuery, ApolloError } from "@apollo/client";
import { GET_VEHICLE_AVAILABILITY } from "../../../../../graphql/bookings/getTestDriveVehicleAvailability";
import { DateTime as d } from "luxon";
import { BookingType, IAddress } from "../../../../../reducers/bookings/types";
import { Skeleton } from "@material-ui/lab";
import VehicleFeatures from "../../../../VehicleFeatures/VehicleFeatures";
import { ITestDriveVehicleGroup } from "../../../../../reducers/testDriveBooking/types";
import { SnackBarVariant } from "../../../../SnackbarWrapper/SnackbarWrapper";
import { useSnackBar } from "../../../../SnackBarContext/SnackBarContext";


const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: "16px",
    minHeight: "100vh",
    overflowY: "auto",
    transition: "all 0.5s ease",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ddd",
      borderRadius: 3,
    },
  },

  leftPanel: {
    padding: theme.spacing(2, 3),
  },
  rightPanel: {
    padding: theme.spacing(2, 3),
  },

  vehicleImage: {
    width: "100%",
    height: 300,
    objectFit: "contain",
    borderRadius: 14,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      height: 200,
    }
  },
  specBox: {
    display: "flex",
    overflowX: "auto",
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    "&::-webkit-scrollbar": { height: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: 3,
    },
    justifyContent: "space-around",
  },
  specItem: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 1),
    border: "1px solid #eee",
    borderRadius: 12,
    backgroundColor: "#fafafa",
    marginRight: theme.spacing(1.5),
    "&:hover": {
      backgroundColor: "#f3f3f3",
    },
  },
  specLabel: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#111",
    marginLeft: theme.spacing(0.5),
  },

  formSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    border: "1px solid #f0f0f0",
    borderRadius: 14,
    backgroundColor: "#fafafa",
  },
  
  formControl: {
    minWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  
  selectSlot: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 12,
    },
    "& .MuiOutlinedInput-input": { cursor: "pointer" },
  },
  select:{
    backgroundColor:"#fff",
    borderRadius: 8,
    border:"1px solid #cbcbcbff",
    padding:6
  },
  text: { 
    marginTop: theme.spacing(2), 
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    color: "#111",
  },

  footer: {
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 6),
    minHeight: 72,
    borderTop: "1px solid #f0f0f0",
    zIndex: 10,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      height: 60,
      justifyContent: "space-between",
      padding: theme.spacing(2, 3),
    }
  },
  
  pageContent: {
    height: "calc(100vh - 72px)",
    overflowY: "auto",
    paddingBottom: 24,
  },

  ctaButton: {
    borderRadius: 40,
    padding: "12px 36px",
    fontWeight: 700,
    fontSize: 16,
    textTransform: "none",
    borderWidth: 2,
    borderColor: "#111 !important",
    color: "#111 !important",
    transition: "all 240ms cubic-bezier(0.4, 0, 0.2, 1)",
    backdropFilter: "blur(6px)",
    "&:hover": {
      borderColor: "#000",
      background: "rgba(0,0,0,0.04)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.5),
      fontSize: 14,
    }
  },

  ctaSelected: {
    background: "rgba(17, 17, 17, 0.9) !important",
    borderColor: "#000 !important",
    color: "#fff !important",
    "&:hover": {
      background: "rgba(17, 17, 17, 1) !important",
      transform: "translateY(-1px)",
      boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
    },
  },
}));

interface IVehicleSlotSelection {
  testDriveType: TestDriveTypes;
  testDriveExperience: TestDriveExperiences;
  selectedPickupSlot: string;
  selectedDropoffSlot: string;
  selectedDate: string;
  vehicleId: string;
  calendarPickupInitialSlot: number;
  calendarDropoffInitialSlot: number;
  extendedTestDriveDuration: number;
  pickupSlot?: IDriverSlot;
  dropoffSlot?: IDriverSlot;
  pickupOtherLocation: IAddress;
  deliveryType: DeliveryType;
}

interface IVehicleAvailability {
  driverId: string;
  slotId: string;
  timeSlot: string;
}

interface IDriverSlot {
  driverId: string;
  slotId: string;
}

interface IProps {
  vehicleGroup: ITestDriveVehicleGroup;
  selectedVehicleId: string;
  selectedVehicleIndex: number;
  processedImages: {[key: string]: string};
}

const TestDriveVehicleDetailDrawer: React.FC<IProps> = ({ vehicleGroup, selectedVehicleId, selectedVehicleIndex, processedImages }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const snackbar = useSnackBar();
  
  const displayName = vehicleGroup.vehicleIds.length > 1 
    ? `${vehicleGroup.vehicleName} #${selectedVehicleIndex + 1}` 
    : vehicleGroup.vehicleName;
  
  const initialVehicleSelection: IVehicleSlotSelection = {
    testDriveType: TestDriveTypes.STANDARD,
    testDriveExperience: TestDriveExperiences.CONSULTATION,
    vehicleId: "",
    selectedDate: d.now().toUTC().toISO(),
    extendedTestDriveDuration: 1,
    pickupOtherLocation: {
      street: "",
      city: "",
      fullAddress: "",
      state: "",
      country: "",
      zipcode: "",
      geometry: {
        type: "",
        latitude: 0,
        longitude: 0,
      },
    },
    selectedPickupSlot: "",
    selectedDropoffSlot: "",
    calendarPickupInitialSlot: 0,
    calendarDropoffInitialSlot: 0,
    deliveryType: DeliveryType.DEALERSHIP,
  };

  const [vehicleSlotSelection, setVehicleSelection] = useState<IVehicleSlotSelection>(initialVehicleSelection);
  const [processedImageUrl, setProcessedImageUrl] = useState<string>(
    getVehicleTypeIcon(capitalizeFirstLetter(vehicleGroup?.bodyType || ""))
  );
  const [openSlotPicker, setSlotPickerState] = useState<boolean>(false);
  const [slot, setSlot] = useState<string>("");
  const [updatedPickupDate, setPickupDate] = useState<string>("");
  const [updatedDropoffDate, setDropoffDate] = useState<string>("");
  const [vehicleAvailabilityDropOffSlots, setVehicleAvailabilityDropOffSlots] = useState<IVehicleAvailability[]>([]);
  const [vehicleAvailabilityPickUpSlots, setVehicleAvailabilityPickUpSlots] = useState<IVehicleAvailability[]>([]);
  const { t } = useTranslation();

  const testDriveBookingState = useSelector((state: IAppState) => state.testDriveBookingReducer);
  const appSetting = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { vehicleDetailsSettings } = website;
  const currentBranch = appSetting.branches.find((branch) => branch.id === testDriveBookingState.branchId);
  const organisation = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const extendedTestDriveMaxHours = organisation.extendedTestDriveMaxHours;
  const standardTestDriveDuration = organisation.standardTestDriveDuration;
  const enableExtendedTestDrive = organisation.enableExtendedTestDrive;
  const filteredTestDriveTypes = testDriveTypesOptions.filter(
    (option) => option.value === TestDriveTypes.STANDARD || enableExtendedTestDrive
  );
  const isExtendedTestDrive = vehicleSlotSelection.testDriveType === TestDriveTypes.EXTENDED;

  const transformAddress = (address: any) => {
    if (!address) return undefined;
    let geometry = address.geometry;
    if (!geometry && address.location) {
      geometry = {
        type: address.location.type || "",
        latitude: address.location.latitude || 0,
        longitude: address.location.longitude || 0
      };
    }
    return {
      street: address.street || "",
      city: address.city || "",
      fullAddress: address.fullAddress || "",
      state: address.state || "",
      country: address.country || "",
      zipcode: address.zipcode || "",
      geometry: geometry || {
        type: "",
        latitude: 0,
        longitude: 0
      }
    };
  };

  const [getTestDriveAvailablilty, { loading: availabilityLoading }] = useLazyQuery(GET_VEHICLE_AVAILABILITY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.getTestDriveVehicleAvailability) {
        const { isExtended, dropoffSlots, pickupSlots } = data.getTestDriveVehicleAvailability;
        if (isExtended && (dropoffSlots?.timeSlots.length || pickupSlots?.timeSlots.length)) {
          setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
          setVehicleAvailabilityPickUpSlots(pickupSlots?.timeSlots || []);
          const selectedDate = d.fromISO(vehicleSlotSelection.selectedDate);
          const dropoffTimeSlot = dropoffSlots?.timeSlots?.[0]?.timeSlot || t("root:na");

          setSlot(`${selectedDate.toLocaleString(d.DATE_FULL)}  | ${dropoffTimeSlot}`);

          const newPickupTime = d
            .fromISO(vehicleSlotSelection.selectedDate, {
              zone: currentBranch?.timeZone,
            })
            .startOf("day")
            .plus({
              hours: d.fromFormat(dropoffSlots?.timeSlots?.[0]?.timeSlot, "h:mm a").hour,
            });
          const newDropoffTime = newPickupTime.plus({
            hours: isExtendedTestDrive && extendedTestDriveMaxHours
              ? extendedTestDriveMaxHours
              : testDriveBookingState?.extendedTestDriveDuration || 1,
          });
          setPickupDate(newPickupTime.toUTC().toISO());
          setDropoffDate(newDropoffTime.toUTC().toISO());
        } else {
          setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
          const selectedDate = d.fromISO(vehicleSlotSelection.selectedDate);
          const dropoffTimeSlot = dropoffSlots?.timeSlots?.[0]?.timeSlot || t("root:na");
          const slotExists = dropoffSlots?.timeSlots?.[0]?.timeSlot;
          
          const newPickupTime = d
            .fromISO(vehicleSlotSelection.selectedDate, {
              zone: currentBranch?.timeZone,
            })
            .startOf("day")
            .plus({
              hours: slotExists ? d.fromFormat(slotExists, "h:mm a").hour : 0,
            });
          
          let newDropoffTime = newPickupTime;
          if (isExtendedTestDrive && extendedTestDriveMaxHours) {
            newDropoffTime = newDropoffTime.plus({ hours: extendedTestDriveMaxHours });
          } else if (standardTestDriveDuration && standardTestDriveDuration.active) {
            newDropoffTime = newDropoffTime.plus({ hours: standardTestDriveDuration.hour });
          } else {
            newDropoffTime = newPickupTime.plus({ hours: vehicleSlotSelection.extendedTestDriveDuration || 1 });
          }
          
          setSlot(`${selectedDate.toLocaleString(d.DATE_FULL)} | ${dropoffTimeSlot}`);
          setPickupDate(newPickupTime.toUTC().toISO());
          setDropoffDate(newDropoffTime.toUTC().toISO());
        }
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });;
    },
  });

  useEffect(() => {
    if (testDriveBookingState.availableVehicleIds && appSetting.id && testDriveBookingState.branchId) {
      getTestDriveAvailablilty({
        variables: {
          availabilityParams: {
            dropoffQueryDate: d.now().plus({ day: 1 }).toUTC().toISO(),
            pickupQueryDate: d.now().toUTC().toISO(),
            vehicleIds: testDriveBookingState.availableVehicleIds,
            branchId: testDriveBookingState.branchId,
            category: BookingType.TEST_DRIVE,
            pickupServiceLocation: testDriveBookingState.pickupLocation,
            dropoffOtherLocation: testDriveBookingState.pickupOtherLocation,
            extendedTestDriveDuration: 1,
          },
        },
      });
    }
  }, [testDriveBookingState.availableVehicleIds, appSetting.id, testDriveBookingState.branchId, getTestDriveAvailablilty]);

  useEffect(() => {
    if (vehicleGroup?.imageUrl && processedImages[vehicleGroup.imageUrl]) {
      setProcessedImageUrl(processedImages[vehicleGroup.imageUrl]);
    } else {
      setProcessedImageUrl(getVehicleTypeIcon(capitalizeFirstLetter(vehicleGroup?.bodyType || "")));
    }
  }, [vehicleGroup?.imageUrl, vehicleGroup?.bodyType, processedImages]);


  const handleSlotPickerClose = () => {
    setSlotPickerState(false);
  };

  const nextStep = () => {
    dispatch(
      updateTestDriveBooking({
        ...testDriveBookingState,
        currentStep: TestDriveBookingSteps.PERSONAL_DETAILS,
        vehicleId: selectedVehicleId, 
        vehicleImage: vehicleGroup.imageUrl || "", 
        testDriveBookingDetails: {
          ...testDriveBookingState.testDriveBookingDetails,
          type: vehicleSlotSelection.testDriveType,
          experience: vehicleSlotSelection.testDriveExperience,
        },
        deliveryType: vehicleSlotSelection.deliveryType,
        pickupDate: updatedPickupDate,
        dropoffDate: updatedDropoffDate,
        pickupSlot: vehicleSlotSelection.pickupSlot,
        dropoffSlot: vehicleSlotSelection.dropoffSlot,
        stepperData: {
          ...testDriveBookingState.stepperData,
          slot: slot,
          model: displayName, 
        },
      })
    );
  };

  return (
    <Box className={classes.drawerPaper}>
      <Box className={classes.pageContent}>
        <Grid container>
          <Grid item xs={12} md={7} className={classes.leftPanel}>
            <img
              src={processedImageUrl}
              alt={displayName}
              className={classes.vehicleImage}
            />

           { vehicleDetailsSettings?.showVehicleOnGroup && vehicleDetailsSettings?.categories?.specification?.enabled && <Box className={classes.specBox}>
              {[
                { icon: getFeatureTypeIcon("numberOfDoors"), label: `${vehicleGroup.doors} Door` , visibility: vehicleDetailsSettings?.categories?.specification?.fields.numberOfDoors},
                { icon: getFeatureTypeIcon("numberOfSeats"), label: `${vehicleGroup.seats} Seats`, visibility: vehicleDetailsSettings?.categories?.specification?.fields.numberOfSeats},
                { icon: getFeatureTypeIcon("fuelType"), label: vehicleGroup.fuelType, visibility: vehicleDetailsSettings?.categories?.specification?.fields.fuelType},
                { icon: getFeatureTypeIcon("transmission"), label: vehicleGroup.transmission, visibility: vehicleDetailsSettings?.categories?.specification?.fields.transmission},
              ].filter(item => item.visibility).map((s, i) => (
                <Box key={i} className={classes.specItem}>
                  <img src={s.icon} alt={s.label} style={{ width: 16, height: 16 }} />
                  <Typography className={classes.specLabel}>{s.label}</Typography>
                </Box>
              ))}
            </Box>}

           <Grid>
          { vehicleDetailsSettings?.showVehicleOnGroup && vehicleDetailsSettings?.categories && vehicleGroup && (
            (vehicleDetailsSettings.categories.interiorFeatures?.enabled && (vehicleGroup.interiorFeatures?.length || 0) > 0) ||
            (vehicleDetailsSettings.categories.exteriorFeatures?.enabled && (vehicleGroup.exteriorFeatures?.length || 0) > 0) ||
            (vehicleDetailsSettings.categories.safetyAndTech?.enabled && (vehicleGroup.safetyAndTechnicalFeatures?.length || 0) > 0)
          ) && (
            <>
              <Typography variant="h1" style={{ fontWeight: 500, marginTop: 24, marginBottom: 8 }}>{t("root:features")}</Typography>
              <VehicleFeatures
                vehicle={{
                  ...vehicleGroup,
                  interiorFeatures: vehicleGroup.interiorFeatures || [],
                  exteriorFeatures: vehicleGroup.exteriorFeatures || [],
                  safetyAndTechnicalFeatures: vehicleGroup.safetyAndTechnicalFeatures || [],
                  otherFeatures: vehicleGroup.features || [],
                } as any}
                showInteriorFeatures={vehicleDetailsSettings?.categories?.interiorFeatures?.enabled}
                showExteriorFeatures={vehicleDetailsSettings?.categories?.exteriorFeatures?.enabled}
                showSafetyAndTech={vehicleDetailsSettings?.categories?.safetyAndTech?.enabled}
                showOtherFeatures={vehicleDetailsSettings?.categories?.otherFeatures?.enabled}
              />
            </>
          )}
        </Grid>
          </Grid>

          <Grid item xs={12} md={5} className={classes.rightPanel}>
            <Typography variant="h1" style={{ fontWeight: 700, marginBottom: 8 }}>
              {displayName}
            </Typography>
            <Typography variant="caption" style={{ color: "#666", marginBottom: 24 }}>
              {t("root:bookATestDrive")}
            </Typography>

            <Formik
              enableReinitialize
              initialValues={vehicleSlotSelection}
              onSubmit={nextStep}
            >
              {() => (
                <form onSubmit={(e) => { e.preventDefault(); nextStep(); }}>
                  <Box className={classes.formSection}>
                    <FormControl className={classes.formControl}>
                      <Typography variant="body1" className={classes.text}>
                        Test Drive Type
                      </Typography>
                      <Select
                        displayEmpty
                        value={vehicleSlotSelection.testDriveType}
                        onChange={(event) => {
                          const newType = event.target.value as TestDriveTypes;
                          setVehicleSelection({
                            ...vehicleSlotSelection,
                            extendedTestDriveDuration: 1,
                            testDriveType: newType,
                          });
                          
                          if (testDriveBookingState.availableVehicleIds && appSetting.id && testDriveBookingState.branchId) {
                            getTestDriveAvailablilty({
                              variables: {
                                availabilityParams: {
                                  dropoffQueryDate: d.now().plus({ day: 1 }).toUTC().toISO(),
                                  pickupQueryDate: d.now().toUTC().toISO(),
                                  vehicleIds: testDriveBookingState.availableVehicleIds,
                                  branchId: testDriveBookingState.branchId,
                                  category: BookingType.TEST_DRIVE,
                                  pickupServiceLocation: testDriveBookingState.pickupLocation,
                                  dropoffOtherLocation: transformAddress(testDriveBookingState.pickupOtherLocation),
                                  extendedTestDriveDuration: vehicleSlotSelection.extendedTestDriveDuration,
                                },
                              },
                            });
                          }
                        }}
                        name="testDriveType"
                        className={classes.select}
                      >
                        {filteredTestDriveTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl className={classes.formControl}>
                      <Typography variant="body1" className={classes.text}>
                        Experience
                      </Typography>
                      <Select
                        displayEmpty
                        value={vehicleSlotSelection.testDriveExperience}
                        onChange={(event) => {
                          setVehicleSelection({
                            ...vehicleSlotSelection,
                            testDriveExperience: event.target.value as TestDriveExperiences,
                          });
                        }}
                        name="testDriveExperience"
                        className={classes.select}
                      >
                        {testDriveExperienceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl className={classes.formControl}>
                      <Typography variant="body1" className={classes.text}>
                        Delivery Type
                      </Typography>
                      <RadioGroup
                        row
                        aria-labelledby="delivery-type-radio-group-label"
                        name="deliveryType"
                        value={vehicleSlotSelection.deliveryType}
                        onChange={(e) => {
                          const deliveryType = e.target.value as DeliveryType;
                          setVehicleSelection({
                            ...vehicleSlotSelection,
                            deliveryType: deliveryType,
                          });
                        }}
                      >
                        <FormControlLabel
                          value={DeliveryType.DEALERSHIP}
                          control={<Radio />}
                          label="At Dealership"
                        />
                        <FormControlLabel
                          value={DeliveryType.HOME}
                          control={<Radio />}
                          label="At Home"
                        />
                      </RadioGroup>
                    </FormControl>

                    <FormControl className={classes.formControl}>
                      <Typography variant="body1" className={classes.text}>
                        {t("root:selectSlot")}
                      </Typography>
                      {availabilityLoading ? (
                        <Skeleton variant="rect" animation="wave" height={40} />
                      ) : vehicleSlotSelection.selectedDate &&
                        vehicleAvailabilityDropOffSlots.length ? (
                        <TextField
                          id={`-select`}
                          value={slot}
                          onClick={() => setSlotPickerState(true)}
                          className={classes.selectSlot}
                          InputProps={{
                            readOnly: true,
                            style: { cursor: "pointer" },
                          }}
                        ></TextField>
                      ) : (
                        <TextField
                          id={`-select`}
                          value={`Vehicle Not Available on Selected Date`}
                          onClick={() => setSlotPickerState(true)}
                          className={classes.selectSlot}
                          InputProps={{
                            readOnly: true,
                            style: { cursor: "pointer" },
                          }}
                        ></TextField>
                      )}
                    </FormControl>
                  </Box>

                  <Box mt={3}>
                    <Button
                      type="submit"
                      variant="outlined"
                      className={`${classes.ctaButton} ${slot ? classes.ctaSelected : ""}`}
                      startIcon={<CheckRounded />}
                      fullWidth
                      disabled={!slot}
                      style={{
                        backgroundColor: !slot ? "#e0e0e0" : undefined,
                        color: !slot ? "#999" : undefined,
                        borderColor: !slot ? "#ccc" : undefined,
                      }}
                    >
                      Continue to Personal Details
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Box>

      <CalendarTimeSlotSelector
        dialogOpen={openSlotPicker}
        bookingType={vehicleSlotSelection.testDriveType}
        loading={availabilityLoading}
        vehicleAvailabilityDropOffSlots={vehicleAvailabilityDropOffSlots || []}
        vehicleAvailabilityPickUpSlots={vehicleAvailabilityPickUpSlots || []}
        handleDialogState={handleSlotPickerClose}
        fetchAvailability={(queryDateString: string) => {
          getTestDriveAvailablilty({
            variables: {
              availabilityParams: {
                dropoffQueryDate: d
                  .fromISO(queryDateString)
                  .plus({ day: 1 })
                  .toUTC()
                  .toISO(),
                pickupQueryDate: d
                  .fromISO(queryDateString)
                  .toUTC()
                  .toISO(),
                vehicleIds: testDriveBookingState.availableVehicleIds,
                branchId: testDriveBookingState.branchId,
                category: BookingType.TEST_DRIVE,
                pickupServiceLocation: testDriveBookingState.pickupLocation,
                dropoffOtherLocation: transformAddress(testDriveBookingState.pickupOtherLocation),
                extendedTestDriveDuration: vehicleSlotSelection.extendedTestDriveDuration,
              },
            },
          });
        }}
        updateSlot={(data) => setSlot(data)}
        updateSelectedDate={(val) =>
          setVehicleSelection({
            ...vehicleSlotSelection,
            selectedDate: val,
          })
        }
        setPickupSlotData={(
          driverId: string,
          slotId: string,
          slotTime: string
        ) => {
          const newPickupTime = d
            .fromISO(vehicleSlotSelection.selectedDate)
            .startOf("day")
            .plus({ hours: d.fromFormat(slotTime, "h:mm a").hour });
          let newDropoffTime = newPickupTime;
          if (isExtendedTestDrive && extendedTestDriveMaxHours) {
            newDropoffTime = newPickupTime.plus({
              hours: extendedTestDriveMaxHours,
            });
          } else {
            newDropoffTime = newPickupTime.plus({
              hours: vehicleSlotSelection.extendedTestDriveDuration,
            });
          }
          setPickupDate(newPickupTime.toUTC().toISO());
          setDropoffDate(newDropoffTime.toUTC().toISO());
          setVehicleSelection({
            ...vehicleSlotSelection,
            pickupSlot: {
              driverId,
              slotId,
            },
          });
        }}
        setDropOffSlotData={(
          driverId: string,
          slotId: string,
          slotTime: string
        ) => {
          const newPickupTime = d
            .fromISO(vehicleSlotSelection.selectedDate, {
              zone: currentBranch?.timeZone,
            })
            .startOf("day")
            .plus({ hours: d.fromFormat(slotTime, "h:mm a").hour });
          let newDropoffTime = newPickupTime;
          if (isExtendedTestDrive && extendedTestDriveMaxHours) {
            newDropoffTime = newPickupTime.plus({
              hours: extendedTestDriveMaxHours,
            });
          } else if (
            standardTestDriveDuration &&
            standardTestDriveDuration.active
          ) {
            newDropoffTime = newDropoffTime.plus({
              hours: standardTestDriveDuration.hour,
            });
          } else {
            newDropoffTime = newPickupTime.plus({
              hours:
                vehicleSlotSelection.extendedTestDriveDuration || 1,
            });
          }
          setPickupDate(newPickupTime.toUTC().toISO());
          setDropoffDate(newDropoffTime.toUTC().toISO());

          setVehicleSelection({
            ...vehicleSlotSelection,
            dropoffSlot: {
              driverId,
              slotId,
            },
          });
        }}
      />
    </Box>
  );
};

export default TestDriveVehicleDetailDrawer;